/**
 * What the patient told us on the intake form, on the patient page.
 *
 * Three parts, all read from `GET /patients/:id/intake`:
 *
 *   1. Goals in the patient's own order, 1 to 3, and the primary goal the
 *      clinician confirms. The patient ranks; the clinician confirms, with a
 *      target where the goal is measured (weight loss in lb, lean muscle in lb
 *      of skeletal muscle). That confirmed goal is what the Clinical Twin and
 *      the monthly check-ins measure.
 *   2. The intake answers MCOS has stored since 24 September: baseline
 *      scores, lifestyle, progress photo and consent.
 *   3. The Muscle Growth / Strength module (29 September), including whether
 *      the recovery adjunct flag is set and why.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, Field, InfoHint, inputClass } from './ui';

interface FormAnswers {
  submittedAt?: string;
  baseline?: Record<string, number | string>;
  lifestyle?: Record<string, number | string | string[]>;
  progressPhotoUrl?: string;
  paymentCompleted?: boolean;
  consentSigned?: boolean;
  bodygramConsent?: boolean;
}

interface MuscleProfile {
  successTargets: string[];
  strengthScore: number | null;
  bodyCompositionSatisfaction: number | null;
  resistanceTrainingDaysBand: string | null;
  exerciseTypes: string[];
  trainingConsistency: string | null;
  proteinIntakeContext: string | null;
  limitingFactors: string[];
  trainingLimitedByInjury: boolean | null;
  injuryLocations: string[];
  concurrentWeightLossGoal: boolean | null;
  glp1GipContext: string | null;
  bodyCompositionSource: string | null;
  recoveryScore: number | null;
  recoveryLimitationFlags: string[];
  recoveryAdjunctEligible: boolean;
  recoveryAdjunctReasons: string[];
  submittedAt: string;
}

interface IntakeResponse {
  intake: { goals: string[]; formAnswers: FormAnswers | null } | null;
  flags?: {
    muscleModuleActive: boolean;
    bodyCompositionRequired: boolean;
    recoveryAdjunctEligible: boolean;
    injuryReview: boolean;
  };
  confirmedGoal: {
    primaryGoal: string;
    targetValue: number | null;
    targetUnit: string | null;
    setAt: string | null;
  } | null;
  muscleProfile: MuscleProfile | null;
}

const BASELINE_LABEL: Record<string, string> = {
  energy: 'Energy',
  sleepQuality: 'Sleep quality',
  strength: 'Strength and exercise recovery',
  wellbeing: 'Overall wellbeing',
  weightMetabolism: 'Weight and metabolism',
  mentalClarity: 'Mental clarity',
  stressManagement: 'Stress management',
};

const LIFESTYLE_LABEL: Record<string, string> = {
  exerciseDaysPerWeek: 'Exercise days per week',
  exerciseType: 'Exercise type',
  sleepDuration: 'Typical sleep',
  activityLevel: 'Daily activity',
};

/** Goals measured against a number, and the unit that number is in. */
const MEASURED_UNIT: Record<string, string> = {
  'weight loss': 'lb',
  'lean muscle': 'lb',
};

const REASON_LABEL: Record<string, string> = {
  recovery_score_low: 'Low exercise recovery score',
  injury_limits_training: 'Pain or injury limits training',
};

function reasonText(reason: string): string {
  if (REASON_LABEL[reason]) return REASON_LABEL[reason]!;
  return reason.startsWith('recovery_problem:') ? reason.slice('recovery_problem:'.length) : reason;
}

function sentence(tag: string): string {
  return tag.charAt(0).toUpperCase() + tag.slice(1);
}

function listText(value: number | string | string[]): string {
  return Array.isArray(value) ? value.join(', ') : String(value);
}

export function IntakeFormCard({ patientId, canEdit }: { patientId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['intake', patientId],
    queryFn: () => api.get<IntakeResponse>(`/patients/${patientId}/intake`),
  });

  const goals = query.data?.intake?.goals ?? [];
  const confirmed = query.data?.confirmedGoal ?? null;
  const answers = query.data?.intake?.formAnswers ?? null;
  const muscle = query.data?.muscleProfile ?? null;
  const flags = query.data?.flags ?? null;

  const [goal, setGoal] = useState('');
  const [target, setTarget] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setGoal(confirmed?.primaryGoal ?? goals[0] ?? '');
    setTarget(confirmed?.targetValue != null ? String(confirmed.targetValue) : '');
    // Re-seed only when the server's answer changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmed?.primaryGoal, confirmed?.targetValue, goals.join('|')]);

  const unit = MEASURED_UNIT[goal] ?? null;
  const confirm = useMutation({
    mutationFn: () =>
      api.patch(`/patients/${patientId}`, {
        primaryGoal: goal,
        goalTargetValue: unit && target.trim() ? Number(target) : null,
        goalTargetUnit: unit && target.trim() ? unit : null,
      }),
    onSuccess: () => {
      setMessage('Primary goal confirmed.');
      void queryClient.invalidateQueries({ queryKey: ['intake', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
    },
    onError: (caught) => setMessage(caught instanceof Error ? caught.message : 'Could not save the goal'),
  });

  if (query.isLoading || !query.data) return null;
  if (goals.length === 0 && !answers && !muscle && !confirmed) return null;

  const choices = [...new Set([...goals, ...(confirmed ? [confirmed.primaryGoal] : [])])];
  const targetValid = !unit || target.trim() === '' || Number(target) > 0;

  return (
    <Card title="Intake form" subtitle="What the patient told us, in their own order.">
      <div className="space-y-5 text-sm">
        {/* 1. Ranked goals and the confirmed primary goal. */}
        <section aria-labelledby="ranked-goals">
          <h3 id="ranked-goals" className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ink-muted">
            Goals, ranked by the patient
            <InfoHint label="About ranked goals">
              The patient ranks two goals on the intake form, primary and secondary. The clinician
              confirms the primary goal here; that is the goal the Clinical Twin and the monthly check-ins
              measure. Weight loss and lean muscle take a target in lb.
            </InfoHint>
          </h3>
          {goals.length === 0 ? (
            <p className="mt-1 text-xs text-ink-muted">No goals on the intake form yet.</p>
          ) : (
            <ol className="mt-1.5 space-y-1">
              {goals.map((tag, index) => (
                <li key={tag} className="flex items-center gap-2 text-ink">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-ink/5 text-[11px] font-semibold text-ink-muted">
                    {index + 1}
                  </span>
                  {sentence(tag)}
                </li>
              ))}
            </ol>
          )}
          <p className="mt-2 text-xs text-ink-muted" data-testid="confirmed-goal">
            {confirmed
              ? `Confirmed primary goal: ${sentence(confirmed.primaryGoal)}${
                  confirmed.targetValue != null ? `, target ${confirmed.targetValue} ${confirmed.targetUnit ?? ''}`.trimEnd() : ''
                }`
              : 'No primary goal confirmed yet.'}
          </p>
          {canEdit && choices.length > 0 && (
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <Field label="Primary goal">
                <select className={inputClass} value={goal} onChange={(event) => setGoal(event.target.value)}>
                  {choices.map((tag) => (
                    <option key={tag} value={tag}>
                      {sentence(tag)}
                    </option>
                  ))}
                </select>
              </Field>
              {unit && (
                <Field label={`Target (${unit})`} hint="Optional">
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.5"
                    className={`${inputClass} w-28`}
                    value={target}
                    onChange={(event) => setTarget(event.target.value)}
                  />
                </Field>
              )}
              <Button
                variant="secondary"
                disabled={!goal || !targetValid}
                loading={confirm.isPending}
                onClick={() => confirm.mutate()}
              >
                Confirm primary goal
              </Button>
            </div>
          )}
          {message && <p className="mt-2 text-xs text-ink-muted">{message}</p>}
        </section>

        {/* The flags the app sets from the goals and answers. */}
        {flags && (
          <ul className="flex flex-wrap gap-1.5 text-[11px]" data-testid="flags" aria-label="Flags">
            {flags.muscleModuleActive && <li className="rounded-full bg-ink/5 px-2 py-0.5 text-ink">Muscle module on</li>}
            {flags.bodyCompositionRequired && <li className="rounded-full bg-ink/5 px-2 py-0.5 text-ink">Body scan required</li>}
            {flags.recoveryAdjunctEligible && <li className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-900">Recovery adjunct eligible</li>}
            {flags.injuryReview && <li className="rounded-full bg-red-100 px-2 py-0.5 text-red-800">Injury review</li>}
            {!flags.muscleModuleActive && !flags.bodyCompositionRequired && !flags.recoveryAdjunctEligible && !flags.injuryReview && (
              <li className="text-ink-muted">No flags set</li>
            )}
          </ul>
        )}

        {/* 2. Baseline scores, lifestyle, photo and consent (T1). */}
        {answers && (
          <section aria-labelledby="intake-answers" className="border-t border-ink/5 pt-4">
            <h3 id="intake-answers" className="text-xs font-medium uppercase tracking-wide text-ink-muted">
              Baseline scores
            </h3>
            {answers.baseline && Object.keys(answers.baseline).length > 0 ? (
              <dl className="mt-1.5 space-y-1">
                {Object.entries(answers.baseline).map(([key, value]) => (
                  <div key={key} className="flex justify-between gap-3">
                    <dt className="text-ink-muted">{BASELINE_LABEL[key] ?? key}</dt>
                    <dd className="shrink-0 whitespace-nowrap font-medium text-ink">{typeof value === 'number' ? `${value} of 10` : value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="mt-1 text-xs text-ink-muted">Not answered.</p>
            )}

            {answers.lifestyle && Object.keys(answers.lifestyle).length > 0 && (
              <>
                <h3 className="mt-4 text-xs font-medium uppercase tracking-wide text-ink-muted">Lifestyle</h3>
                <dl className="mt-1.5 space-y-1">
                  {Object.entries(answers.lifestyle).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-3">
                      <dt className="text-ink-muted">{LIFESTYLE_LABEL[key] ?? key}</dt>
                      <dd className="text-right font-medium text-ink">{listText(value)}</dd>
                    </div>
                  ))}
                </dl>
              </>
            )}

            <ul className="mt-4 space-y-1 text-xs text-ink-muted">
              <li>Consent {answers.consentSigned ? 'signed on the form' : 'not signed on the form'}</li>
              {answers.paymentCompleted !== undefined && (
                <li>Payment {answers.paymentCompleted ? 'completed on the form' : 'not completed on the form'}</li>
              )}
              {answers.progressPhotoUrl && (
                <li>
                  <a href={answers.progressPhotoUrl} target="_blank" rel="noreferrer" className="font-medium text-forest underline">
                    Open the intake progress photo
                  </a>
                </li>
              )}
              {answers.submittedAt && <li>Submitted {new Date(answers.submittedAt).toLocaleDateString()}</li>}
            </ul>
          </section>
        )}

        {/* 3. Muscle Growth / Strength module. */}
        {muscle && (
          <section aria-labelledby="muscle-module" className="border-t border-ink/5 pt-4">
            <h3 id="muscle-module" className="text-xs font-medium uppercase tracking-wide text-ink-muted">
              Muscle growth and recovery
            </h3>
            {muscle.recoveryAdjunctEligible && (
              <div className="mt-2 rounded-sm2 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="recovery-flag">
                <strong className="font-semibold">Recovery issue: </strong>
                {muscle.recoveryAdjunctReasons.map(reasonText).join('; ')}
              </div>
            )}
            <dl className="mt-2 space-y-1">
              {muscle.successTargets.length > 0 && <Row label="Success looks like" value={muscle.successTargets.join(', ')} />}
              {muscle.strengthScore !== null && <Row label="Strength" value={`${muscle.strengthScore} of 10`} />}
              {muscle.recoveryScore !== null && <Row label="Recovery" value={`${muscle.recoveryScore} of 10`} />}
              {muscle.bodyCompositionSatisfaction !== null && (
                <Row label="Body composition satisfaction" value={`${muscle.bodyCompositionSatisfaction} of 10`} />
              )}
              {muscle.resistanceTrainingDaysBand && <Row label="Strength training" value={`${muscle.resistanceTrainingDaysBand} a week`} />}
              {muscle.trainingConsistency && <Row label="Consistency, last 4 weeks" value={muscle.trainingConsistency} />}
              {muscle.proteinIntakeContext && <Row label="Protein" value={muscle.proteinIntakeContext} />}
              {muscle.limitingFactors.length > 0 && <Row label="Limiting factors" value={muscle.limitingFactors.join(', ')} />}
              {muscle.recoveryLimitationFlags.length > 0 && (
                <Row label="Recovery problems" value={muscle.recoveryLimitationFlags.join(', ')} />
              )}
              {muscle.trainingLimitedByInjury !== null && (
                <Row
                  label="Pain or injury limits training"
                  value={muscle.trainingLimitedByInjury ? `Yes${muscle.injuryLocations.length ? `: ${muscle.injuryLocations.join(', ')}` : ''}` : 'No'}
                />
              )}
              {muscle.concurrentWeightLossGoal !== null && (
                <Row label="Also losing weight" value={muscle.concurrentWeightLossGoal ? 'Yes' : 'No'} />
              )}
              {muscle.glp1GipContext && <Row label="GLP-1 or GIP medication" value={muscle.glp1GipContext} />}
            </dl>
          </section>
        )}
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="max-w-[60%] text-right font-medium text-ink">{value}</dd>
    </div>
  );
}
